import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Form } from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Loader2, ChevronRight } from 'lucide-react';
import { PersonalStatusSection } from '../sections/PersonalStatusSection';
import { IncomeSection } from '../sections/IncomeSection';
import { AssetsSection } from '../sections/AssetsSection';
import { NursingHomeSection } from '../sections/NursingHomeSection';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { calculatorSchema } from '../schema';
import type { z } from 'zod';
type FormValues = z.infer<typeof calculatorSchema>;
interface CalculatorFormProps {
  onSubmit: (data: FormValues) => Promise<void>;
  isCalculating: boolean;
}
export function CalculatorForm({
  onSubmit,
  isCalculating
}: CalculatorFormProps) {
  const [activeTab, setActiveTab] = React.useState('personal');
  const form = useForm<FormValues>({
    resolver: zodResolver(calculatorSchema),
    defaultValues: {
      applicantStatus: 'single',
      dependentSpouse: false,
      otherDependents: false,
      grossIncome: 0,
      incomeTax: 0,
      usc: 0,
      prsi: 0,
      propertyTax: 0,
      homeLoanInterest: 0,
      maintenancePayments: 0,
      healthExpenses: 0,
      savingsAndCash: 0,
      investments: 0,
      otherAssets: 0,
      ownsPrimaryResidence: true,
      primaryResidenceValue: 0,
      hasOtherProperties: false,
      otherPropertiesValue: 0,
      hasFarmOrBusiness: false,
      farmBusinessValue: 0,
      familySuccessor: false,
      nursingHomeCost: 0,
      useLoan: false,
      yearsInCare: 0
    }
  });
  const handleTabAdvance = () => {
    const tabs = ['personal', 'income', 'assets', 'nursing'];
    const currentIndex = tabs.indexOf(activeTab);
    if (currentIndex < tabs.length - 1) {
      // Make sure we're setting the correct next tab
      const nextTab = tabs[currentIndex + 1];
      console.log(`Advancing from ${activeTab} to ${nextTab}`);
      setActiveTab(nextTab);
    }
  };

  // Watch values for conditional rendering
  const applicantStatus = form.watch('applicantStatus');
  const ownsPrimaryResidence = form.watch('ownsPrimaryResidence');
  const hasOtherProperties = form.watch('hasOtherProperties');
  const hasFarmOrBusiness = form.watch('hasFarmOrBusiness');
  return <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="p-6 space-y-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} defaultValue="personal" className="w-full">
          <TabsList className="grid grid-cols-4 mb-6">
            <TabsTrigger value="personal">Personal Status</TabsTrigger>
            <TabsTrigger value="income">Income</TabsTrigger>
            <TabsTrigger value="assets">Assets</TabsTrigger>
            <TabsTrigger value="nursing">Nursing Home</TabsTrigger>
          </TabsList>
          
          <TabsContent value="personal">
            <PersonalStatusSection control={form.control} applicantStatus={applicantStatus} />
            
            <div className="pt-4 border-t border-gray-200 mt-6">
              <Button type="button" onClick={() => setActiveTab('income')} className="w-full bg-grandpal-green-dark hover:bg-grandpal-green-dark/90">
                Continue
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </TabsContent>
          
          <TabsContent value="income">
            <IncomeSection control={form.control} applicantStatus={applicantStatus} />
            
            <div className="pt-4 border-t border-gray-200 mt-6">
              <Button type="button" onClick={() => setActiveTab('assets')} className="w-full bg-grandpal-green hover:bg-grandpal-green/90">
                Continue
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </TabsContent>
          
          <TabsContent value="assets">
            <AssetsSection control={form.control} applicantStatus={applicantStatus} ownsPrimaryResidence={ownsPrimaryResidence} hasOtherProperties={hasOtherProperties} hasFarmOrBusiness={hasFarmOrBusiness} />
            
            <div className="pt-4 border-t border-gray-200 mt-6">
              <Button type="button" onClick={() => setActiveTab('nursing')} className="w-full bg-grandpal-green hover:bg-grandpal-green/90">
                Continue
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </TabsContent>
          
          <TabsContent value="nursing">
            <NursingHomeSection control={form.control} ownsPrimaryResidence={ownsPrimaryResidence} hasOtherProperties={hasOtherProperties} hasFarmOrBusiness={hasFarmOrBusiness} />
            
            <div className="pt-4 border-t border-gray-200 mt-6">
              <Button type="submit" className="w-full bg-grandpal-green hover:bg-grandpal-green/90" disabled={isCalculating}>
                {isCalculating ? <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Calculating...
                  </> : "Calculate My Contribution"}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </form>
    </Form>;
}